import React, { useState, useEffect } from 'react';
import styled from '@emotion/styled';
import ReactPlayer from 'react-player';
import moment from 'moment';

import { getColor, ColorType } from './UICommon';
import { Group } from '../../models/Group';
import { useMst } from '../../models/rootModel';

import { CardAvatar } from '../styledComponent/CardAvatar.style';
import { GroupsDetailsProps } from '../steps/PickAGroupStep/pickAGroupStep.types';

import { MainTitleDescription } from './MainTitleDescription';
import { SecondaryTitleDescription } from './SecondaryTitleDescription';
import { PointList } from './PointList';
import { QuoteCard } from './QuoteCard';
import { StatisticSection } from './StatisticSection';
import { QuestionAndAnswer } from './QuestionAndAnswer';

import clock from '../../assets/clock.svg';
import calendar from '../../assets/calendar.svg';
import user from '../../assets/user.svg';
import { observer } from 'mobx-react-lite';

interface StyleProps {
  color?: ColorType;
}

const RootDiv = styled.div`
  font-weight: 500;
  font-size: 16px;
  line-height: 24px;
  max-width: 375px;
  margin: 0 auto;
`;

const Margin = styled.div`
  margin: 32px;
`;

const InfoSection = styled.div`
  margin: 0 32px;
  padding-bottom: 20px;
  border-bottom: 1px solid ${getColor('BLACK_10')};
`;

const GroupInfoDiv = styled.div`
  display: flex;
  gap: 12px;
  margin-bottom: 12px;
`;

const FacilitatorInfoDiv = styled.div`
  display: flex;
  gap: 16px;
  margin: 32px 0 16px;
`;

const NameDiv = styled.div`
  display: flex;
  flex-direction: column;
  align-self: center;
`;

const FacilitatorName = styled.p`
  font-size: 18px;
  font-weight: 600;
  color: ${getColor('PURPLE_100')};
  line-height: 26px;
`;

const ReadMoreButton = styled.span`
  color: ${getColor('PURPLE_100')};
  font-weight: 600;
  cursor: pointer;
`;
const QuestionsSection = styled.div`
  margin: 32px;
  margin-bottom: 0;
`;

const P = styled.p`
  color: ${(props: StyleProps) => (props.color ? getColor(props.color) : getColor('BLACK'))};
`;

const Player = styled(ReactPlayer)`
  margin-bottom: 32px;
  iframe {
    border-radius: 24px;
  }
`;

export const GroupsDetails = observer((props: GroupsDetailsProps) => {
  const { twelfthWeekProgram, weeklyMeeting, statisticInformation, questionsList, pickedGroupId, stepType } = props;
  const [groupToDisplay, setGroupToDisplay] = useState<Group>();
  const [showMore, setShowMore] = useState(false);
  const { Group } = useMst();
  const date = moment(groupToDisplay?.schedule);

  useEffect(() => {
    // get group
    setGroupToDisplay(Group.groups.find((group) => group.id === pickedGroupId));
  }, [pickedGroupId, Group]);

  return (
    <RootDiv>
      {groupToDisplay ? (
        <>
          <InfoSection>
            <MainTitleDescription title={groupToDisplay.title} isButton={true} stepType={stepType} />
            <GroupInfoDiv>
              <img src={clock} alt="clock" />
              <P>{`${date.format('dddd')} at ${date.format('h')}-${date.add(1, 'hours').format('h A')}`}</P>
            </GroupInfoDiv>
            <GroupInfoDiv>
              <img src={calendar} alt="calendar" />
              <P>{`Starting on ${date.format('MMM DD')}`}</P>
            </GroupInfoDiv>
            <GroupInfoDiv>
              <img src={user} alt="user" />
              <P>4 spots left</P>
            </GroupInfoDiv>
          </InfoSection>

          <InfoSection>
            <FacilitatorInfoDiv>
              <CardAvatar src={groupToDisplay.leaderImg} alt={groupToDisplay.leaderName} size={64} />
              <NameDiv>
                <FacilitatorName>{groupToDisplay.leaderName}</FacilitatorName>
                <P color="BLACK_60">Group facilitator</P>
              </NameDiv>
            </FacilitatorInfoDiv>

            <P color="BLACK_80">
              {showMore ? groupToDisplay.intro : `${groupToDisplay.intro.slice(0, 168)}... `}
              <ReadMoreButton onClick={() => setShowMore(!showMore)}>
                {!showMore ? 'Read more' : ' Read less'}
              </ReadMoreButton>
            </P>
          </InfoSection>
        </>
      ) : (
        ''
      )}

      <Margin>
        <SecondaryTitleDescription {...twelfthWeekProgram} marginBottom={16} />
        <PointList pointList={twelfthWeekProgram.programPoints} />

        {twelfthWeekProgram.videoUrl && twelfthWeekProgram.videoUrl?.length > 0 ? (
          <Player url={twelfthWeekProgram.videoUrl} controls={true} height={'311px'} className="react-player" />
        ) : (
          ''
        )}

        <SecondaryTitleDescription {...weeklyMeeting} marginBottom={16} />
        <PointList pointList={weeklyMeeting.weeklyPoints} />
      </Margin>

      <StatisticSection
        percentageNumber={statisticInformation.percentageNumber}
        description={statisticInformation.description}
        borderRadius={false}
      />

      <QuestionsSection>
        {questionsList.map((questionAndAnswer) => (
          <QuestionAndAnswer key={questionAndAnswer.question} questionAndAnswer={questionAndAnswer} />
        ))}
      </QuestionsSection>

      <QuoteCard {...props} isFullScreen={true} />
    </RootDiv>
  );
});
